import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import * as defaultSkin from './skins/select.default.js';
import * as wireframeSkin from './skins/select.wireframe.js';

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

class RunSelect extends RunElement {
  static formAssociated = true;

  #value = '';
  #name = '';
  #disabled = false;
  #required = false;
  #open = false;
  #placeholder = '';
  #multiple = false;
  #rendering = false;
  #internals = null;
  #activeIndex = -1;
  #options = [];

  static get observedAttributes() {
    return ['value', 'name', 'disabled', 'required', 'open', 'placeholder', 'multiple'];
  }

  // Arrow-function closure properties capture `this` without rebinding.
  // Private methods are not writable — `this.#method = this.#method.bind(this)` throws TypeError.
  _onClickBound = (e) => this.#handleClick(e);
  _onKeydownBound = (e) => this.#handleKeydown(e);
  _onBlurBound = (e) => this.#handleBlur(e);
  _onOptionClickBound = (e) => this.#handleOptionClick(e);
  _onOutsideClickBound = (e) => this.#handleOutsideClick(e);
  _onShadowClickBound = (e) => this.#handleShadowClick(e);

  constructor() {
    super();
    this.#internals = this.attachInternals();
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'combobox');
    this.addEventListener('click', this._onClickBound);
    this.addEventListener('keydown', this._onKeydownBound);
    this.addEventListener('blur', this._onBlurBound);
    this.#updateAria();
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this._onClickBound);
    this.removeEventListener('keydown', this._onKeydownBound);
    this.removeEventListener('blur', this._onBlurBound);
    this.shadowRoot.removeEventListener('click', this._onShadowClickBound);
    document.removeEventListener('click', this._onOutsideClickBound);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'value':       this.value = newValue !== null ? newValue : ''; break;
      case 'name':        this.name = newValue !== null ? newValue : ''; break;
      case 'disabled':    this.disabled = newValue !== null; break;
      case 'required':    this.required = newValue !== null; break;
      case 'open':        this.open = newValue !== null; break;
      case 'placeholder': this.placeholder = newValue !== null ? newValue : ''; break;
      case 'multiple':    this.multiple = newValue !== null; break;
    }
  }

  formResetCallback() {
    this.#value = '';
    this.removeAttribute('value');
    this.#open = false;
    this.removeAttribute('open');
    this.#activeIndex = -1;
    this.#internals.setFormValue(null);
    this.#internals.setValidity({});
    this.#updateAria();
    this.requestUpdate();
  }

  formDisabledCallback(disabled) {
    this.disabled = disabled;
  }

  formStateRestoreCallback(state, mode) {
    if (mode === 'restore' && typeof state === 'string') {
      this.value = state;
    }
  }

  get value() { return this.#value; }
  set value(val) {
    const coerced = String(val ?? '').trim();
    if (this.#value === coerced) return;
    this.#value = coerced;
    if (coerced) {
      this.setAttribute('value', coerced);
    } else {
      this.removeAttribute('value');
    }
    this.#syncFormValue();
    this.#updateAria();
    this.requestUpdate();
  }

  get name() { return this.#name; }
  set name(val) {
    const coerced = String(val ?? '').trim();
    if (this.#name === coerced) return;
    this.#name = coerced;
    if (coerced) {
      this.setAttribute('name', coerced);
    } else {
      this.removeAttribute('name');
    }
    this.requestUpdate();
  }

  get disabled() { return this.#disabled; }
  set disabled(val) {
    const coerced = Boolean(val);
    if (this.#disabled === coerced) return;
    this.#disabled = coerced;
    this.toggleAttribute('disabled', coerced);
    if (coerced && this.#open) {
      this.#closeDropdown();
    }
    this.#updateAria();
    this.requestUpdate();
  }

  get required() { return this.#required; }
  set required(val) {
    const coerced = Boolean(val);
    if (this.#required === coerced) return;
    this.#required = coerced;
    this.toggleAttribute('required', coerced);
    this.#syncValidity();
    this.#updateAria();
    this.requestUpdate();
  }

  get open() { return this.#open; }
  set open(val) {
    const coerced = Boolean(val);
    if (this.#open === coerced) return;
    this.#open = coerced;
    this.toggleAttribute('open', coerced);
    this.#updateAria();
    this.requestUpdate();
  }

  get placeholder() { return this.#placeholder; }
  set placeholder(val) {
    const coerced = String(val ?? '').trim();
    if (this.#placeholder === coerced) return;
    this.#placeholder = coerced;
    if (coerced) {
      this.setAttribute('placeholder', coerced);
    } else {
      this.removeAttribute('placeholder');
    }
    this.requestUpdate();
  }

  get multiple() { return this.#multiple; }
  set multiple(val) {
    const coerced = Boolean(val);
    if (this.#multiple === coerced) return;
    this.#multiple = coerced;
    this.toggleAttribute('multiple', coerced);
    this.#updateAria();
    this.requestUpdate();
  }

  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skin = this.#resolveSkin();
      if (skin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
    } finally {
      this.#rendering = false;
    }
    // RUN-376 R2: the trigger lives in the shadow root and the host `click`
    // listener proved unreliable for it in composed contexts. The shadowRoot
    // node is stable across innerHTML swaps, so a listener here reliably
    // catches trigger clicks. Attached in updated() (not connectedCallback)
    // because RunElement attaches shadowRoot lazily — it is null at connect.
    // Re-adding the same bound listener is a DOM no-op, so this is safe to
    // call every render and survives disconnect/reconnect.
    this.shadowRoot.addEventListener('click', this._onShadowClickBound);
    this.#updateAria();
    this.#refreshOptions();
    this.#syncPopover();
  }

  #resolveSkin() {
    const s = this.getAttribute('skin');
    if (s) return s;
    return resolveSkinFromAncestors(this);
  }

  #updateAria() {
    this.setAttribute('aria-expanded', String(this.#open));
    this.setAttribute('aria-disabled', String(this.#disabled));
    this.setAttribute('aria-required', String(this.#required));
    this.setAttribute('aria-haspopup', 'listbox');
    this.setAttribute('tabindex', this.#disabled ? '-1' : '0');

    if (this.#activeIndex >= 0 && this.#options.length > 0) {
      const activeOption = this.#options[this.#activeIndex];
      if (activeOption && activeOption.id) {
        this.setAttribute('aria-activedescendant', activeOption.id);
      } else if (activeOption) {
        this.removeAttribute('aria-activedescendant');
      }
    } else {
      this.removeAttribute('aria-activedescendant');
    }

    const listbox = this.shadowRoot?.querySelector('[role="listbox"]');
    if (listbox) {
      listbox.setAttribute('aria-multiselectable', String(this.#multiple));
    }
  }

  #syncFormValue() {
    this.#internals.setFormValue(this.#value || null);
    this.#syncValidity();
  }

  #syncValidity() {
    const anchor = this.shadowRoot?.querySelector('[part="trigger"]') ?? undefined;
    if (this.#required && !this.#value) {
      this.#internals.setValidity(
        { valueMissing: true },
        'Please select an option.',
        anchor
      );
    } else {
      this.#internals.setValidity({});
    }
  }

  #refreshOptions() {
    if (!this.shadowRoot) return;
    const hostId = this.id || 'run-select';
    const slot = this.shadowRoot.querySelector('slot:not([name])');
    this.#options = slot
      ? Array.from(slot.assignedElements()).filter(el => el.getAttribute('role') === 'option')
      : [];
    this.#options.forEach((opt, i) => {
      if (!opt.id) {
        opt.id = `${hostId}-option-${i}`;
      }
      opt.setAttribute('aria-selected', String(opt.getAttribute('data-value') === this.#value));
    });
  }

  // Host listener: ONLY handles slotted-option clicks. Slotted options live in
  // light DOM, so their clicks bubble to the host (not through the shadow root).
  // Trigger toggling is owned by #handleShadowClick (reliable across renders).
  #handleClick(event) {
    if (this.#disabled) return;
    if (this.#open) {
      const option = event.target.closest('[role="option"]');
      if (option) {
        this.#handleOptionClick(event);
      }
    }
  }

  // ShadowRoot listener: owns trigger open/close. The shadowRoot node is stable
  // even though its innerHTML is replaced every render, so this fires reliably
  // regardless of host-bubble timing or event-target reconstruction (RUN-376 R2).
  #handleShadowClick(event) {
    const onTrigger = event.composedPath().some(
      (n) => n && n.nodeType === 1 && typeof n.getAttribute === 'function' && n.getAttribute('part') === 'trigger'
    );
    if (this.#disabled) return;
    if (onTrigger) {
      this.#toggleDropdown();
    }
  }

  #handleKeydown(event) {
    if (this.#disabled) return;
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (this.#open) {
          this.#selectActiveOption();
        } else {
          this.#openDropdown();
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!this.#open) {
          this.#openDropdown();
        } else {
          this.#moveActiveIndex(1);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!this.#open) {
          this.#openDropdown();
        } else {
          this.#moveActiveIndex(-1);
        }
        break;
      case 'Home':
        if (this.#open) {
          event.preventDefault();
          this.#setActiveIndex(0);
        }
        break;
      case 'End':
        if (this.#open) {
          event.preventDefault();
          this.#setActiveIndex(this.#options.length - 1);
        }
        break;
      case 'Escape':
        if (this.#open) {
          event.preventDefault();
          this.#closeDropdown();
        }
        break;
      case 'Tab':
        if (this.#open) {
          this.#closeDropdown();
        }
        break;
    }
  }

  #handleBlur(event) {
    if (!this.#open) return;
    const relatedTarget = event.relatedTarget;
    if (relatedTarget && this.contains(relatedTarget)) return;
    this.#closeDropdown();
  }

  #handleOptionClick(event) {
    const option = event.target.closest('[role="option"]');
    if (!option) return;
    if (option.getAttribute('aria-disabled') === 'true') return;
    event.stopPropagation();
    const optionValue = option.getAttribute('data-value') ?? option.textContent.trim();
    this.#selectValue(optionValue);
  }

  #handleOutsideClick(event) {
    // Use composedPath(), not event.target: across nested shadow boundaries the
    // target is retargeted to the outermost host, so a click that originated
    // inside this run-select (e.g. the opening trigger click) appears "outside"
    // and would immediately re-close the just-opened dropdown (RUN-376 R2).
    // composedPath() contains every node the event traversed, including this
    // host and its shadow children, regardless of retargeting.
    const inside = event.composedPath().includes(this);
    if (!inside) {
      this.#closeDropdown();
    }
  }

  #toggleDropdown() {
    if (this.#open) {
      this.#closeDropdown();
    } else {
      this.#openDropdown();
    }
  }

  #openDropdown() {
    if (this.#disabled || this.#open) return;
    this.open = true;
    document.addEventListener('click', this._onOutsideClickBound);
    this.#refreshOptions();
    const currentIndex = this.#options.findIndex(
      (opt) => opt.getAttribute('data-value') === this.#value
    );
    this.#activeIndex = currentIndex >= 0 ? currentIndex : 0;
    this.#updateAria();
    this.#emitOpen();
  }

  #closeDropdown() {
    if (!this.#open) return;
    this.open = false;
    document.removeEventListener('click', this._onOutsideClickBound);
    this.#activeIndex = -1;
    this.#updateAria();
    this.#emitClose();
  }

  #moveActiveIndex(delta) {
    if (this.#options.length === 0) return;
    const next = Math.max(0, Math.min(this.#options.length - 1, this.#activeIndex + delta));
    this.#setActiveIndex(next);
  }

  #setActiveIndex(index) {
    this.#activeIndex = index;
    this.#updateAria();
    const activeOption = this.#options[this.#activeIndex];
    if (activeOption) {
      activeOption.scrollIntoView?.({ block: 'nearest' });
    }
  }

  #selectActiveOption() {
    const activeOption = this.#options[this.#activeIndex];
    if (!activeOption) return;
    if (activeOption.getAttribute('aria-disabled') === 'true') return;
    const optionValue = activeOption.getAttribute('data-value') ?? activeOption.textContent.trim();
    this.#selectValue(optionValue);
  }

  #selectValue(newValue) {
    const previousValue = this.#value;
    this.value = newValue;
    this.#closeDropdown();
    if (previousValue !== newValue) {
      this.#emitChange(newValue, previousValue);
    }
  }

  // Called after every render to ensure the listbox popover state matches #open.
  // shadowRoot.innerHTML replacement creates a fresh element each cycle, so showPopover()
  // must be re-issued on the new element whenever the dropdown should be open.
  #syncPopover() {
    const listbox = this.shadowRoot?.querySelector('[part="listbox"]');
    if (!listbox || !listbox.hasAttribute('popover') || typeof listbox.showPopover !== 'function') return;
    try {
      if (this.#open && !listbox.matches(':popover-open')) {
        listbox.showPopover();
      } else if (!this.#open && listbox.matches(':popover-open')) {
        listbox.hidePopover();
      }
    } catch (_) {
      // Popover API / anchor positioning unsupported — the :host([open])
      // skin rule provides visibility without it (RUN-376 R2).
    }
  }

  #emitChange(value, previousValue) {
    this.dispatchEvent(new CustomEvent('run-change', {
      bubbles: true,
      composed: true,
      detail: { value, previousValue },
    }));
  }

  #emitOpen() {
    this.dispatchEvent(new CustomEvent('run-open', {
      bubbles: true,
      composed: true,
      detail: {},
    }));
  }

  #emitClose() {
    this.dispatchEvent(new CustomEvent('run-close', {
      bubbles: true,
      composed: true,
      detail: {},
    }));
  }
}

if (!customElements.get('run-select')) {
  customElements.define('run-select', RunSelect);
}

export { RunSelect };
export default RunSelect;